// Post-a-listing MOCK — canned content powering the form composer + My listings.
// Ported verbatim from the design prototype (.agent/handoff-web/post-data.jsx).
// THE PRIME DIRECTIVE: this is a careful mock — fake photos, fake AI, no backend.

// ── Types ────────────────────────────────────────────────────────────────────

export interface DraftPhoto {
  id: string
  room: string
  palette: number
  cover?: boolean
  dark?: boolean
  dup?: boolean
}

export interface PostAddress {
  street: string
  district: string
  city: string
  lat: number
  lon: number
}

export interface ExtraFee {
  id: string
  label: string
  amount: number
  custom: boolean
}

export interface ContactSchedule {
  days: string[]
  from: number
  to: number
}

export interface ContactInfo {
  email: string
  phone: string
  telegram: string
  instagram: string
  schedule: ContactSchedule | null
}

export interface PostFacts {
  rooms: number
  area: number
  floor: number
  totalFloors: number
  elevator: boolean
  features: string[]
}

// The draft shape shared by the form composer, the PreviewPanel and
// publishListing (prototype post.jsx EMPTY_DRAFT + postform.jsx draft memo).
export interface PostDraft {
  transaction: 'rent' | 'sale'
  photos: DraftPhoto[] | null
  facts: PostFacts | null
  address: PostAddress | null
  price: number
  deposit: number
  utilities: number
  extras: ExtraFee[]
  tone: string | null
  title: string | null
  body: string | null
  published: boolean
  contact: ContactInfo
}

export type MyListingStatus = 'live' | 'paused' | 'draft' | 'rented'

export interface MyListingUpdate {
  id: string
  text: string
  when: string
}

export interface MyListingSuggestion {
  id: string
  icon: string
  text: string
  action: string
  apply?: Partial<Pick<MyListing, 'price' | 'status'>>
  goPost?: boolean
}

export interface MyListing {
  id: string
  title: string
  status: MyListingStatus
  /** Rent vs sale framing; records saved before this field existed are rent. */
  transaction?: 'rent' | 'sale'
  price: number
  rooms: number
  area: number
  floor: number
  totalFloors: number
  district: string
  palette: number
  images: number
  createdLabel: string
  saves: number
  fresh?: boolean
  updates?: MyListingUpdate[]
  suggestions?: MyListingSuggestion[]
  /** Feature keys shown as chips on the seeker-facing card. */
  features?: string[]
  /** Price breakdown — editable from the dashboard's price editor. */
  utilities?: number
  deposit?: number
  extras?: ExtraFee[]
  /** Full composer snapshot from publish — powers the dashboard preview. */
  draft?: PostDraft
  /** Paid promotion (MOCK — no billing): one week per purchase, expires
   * automatically. Promoted = promoUntil > now. */
  promoUntil?: number
  /** Epoch ms of the last freshness bump — drives the 3-day cooldown. */
  promoLastBump?: number
}

// ── The "camera roll" the seller drops in. dark/dup are the flagged issues ──
export const DRAFT_PHOTOS: DraftPhoto[] = [
  { id: 'p1', room: 'Living room', palette: 0, cover: true },
  { id: 'p2', room: 'Kitchen', palette: 7 },
  { id: 'p3', room: 'Bedroom', palette: 6, dark: true },
  { id: 'p4', room: 'Bathroom', palette: 2 },
  { id: 'p5', room: 'Balcony', palette: 4 },
  { id: 'p6', room: 'View', palette: 1 },
  { id: 'p7', room: 'Hallway', palette: 3 },
  { id: 'p8', room: 'Living room', palette: 0, dup: true },
]

// What the vision pass "reads" from the photos. Feature values are REAL
// keywords_cz feature_keys — anything else would never match a search filter
// nor render on the seeker-facing card (user fix 2026-07-15).
export const DETECTED_FACTS = {
  rooms: 2,
  area: 58,
  floor: 4,
  totalFloors: 6,
  elevator: true,
  features: ['balcony', 'furnished', 'built_in_wardrobes'],
  confidence: { rooms: 'high', area: 'medium', floor: 'ask' },
} as const

// Quick-pick pools — must stay a subset of the live keywords_cz catalogue
// (labels resolve through useTagLabels; unknown keys would dead-end).
export const FEATURE_POOL = [
  'balcony',
  'furnished',
  'built_in_wardrobes',
  'cellar',
  'pets_allowed',
  'air_conditioning',
  'dishwasher',
  'washing_machine',
  'internet',
]
export const FEATURE_EXTRA = [
  'terrace',
  'loggia',
  'garage',
  'parking',
  'storage_room',
  'fireplace',
  'central_heating',
  'quiet',
  'bright',
  'elevator',
]

// Address verification.
export const ADDRESS_SUGGESTION: PostAddress = {
  street: 'Slezská 12',
  district: 'Vinohrady',
  city: 'Praha 2',
  lat: 50.0785,
  lon: 14.4486,
}

// Comparable rentals for the price band. Buckets start at 19 000, step 1 000.
export const PRICE_COMPS = {
  count: 33,
  sample: '2+kk · Vinohrady · last 60 days',
  bucketStart: 19000,
  bucketStep: 1000,
  buckets: [1, 2, 4, 6, 7, 5, 3, 2, 2, 1],
  fairLow: 21800,
  fairHigh: 26400,
  sweet: 23500,
  min: 19000,
  max: 29000,
} as const

export interface PriceVerdict {
  label: string
  note: string
  color: string
}

export function priceVerdict(p: number): PriceVerdict {
  if (p < PRICE_COMPS.fairLow)
    return { label: 'Eager', note: 'Below almost everyone — expect a full inbox in 48 h.', color: 'rgb(var(--info))' }
  if (p > PRICE_COMPS.fairHigh)
    return { label: 'Ambitious', note: 'Above the fair band — it may sit for a few weeks.', color: 'rgb(var(--warning))' }
  if (p <= PRICE_COMPS.sweet)
    return { label: 'Sweet spot', note: 'Fair price, solid steady interest.', color: 'rgb(var(--success))' }
  return { label: 'Confident', note: 'Upper half of fair — fewer but more serious inquiries.', color: 'rgb(var(--success))' }
}

export function inquiriesPerWeek(p: number): number {
  const t = (p - PRICE_COMPS.min) / (PRICE_COMPS.max - PRICE_COMPS.min)
  return Math.max(2, Math.round(20 - t * 18))
}

// Description drafts per tone.
export const TONE_DRAFTS: Record<string, { title: string; body: string }> = {
  Warm: {
    title: 'Sunny 2+kk with a south balcony, Vinohrady',
    body: 'Mornings here start with sun across the parquet — the balcony faces south, so coffee outside is a given from March to October. Inside it’s a freshly renovated 58 m² two-room layout on the 4th floor (with elevator), a quiet one-way street below and Riegrovy sady three minutes on foot. Metro A at Jiřího z Poděbrad is a 4-minute walk.',
  },
  Factual: {
    title: 'Renovated 2+kk, 58 m², Slezská, Vinohrady',
    body: '2+kk, 58 m², 4th floor of 6, elevator. Full renovation 2024: new kitchen, bathroom, oak parquet. South-facing balcony, built-in wardrobe. Jiřího z Poděbrad (metro A) 4 min on foot, Riegrovy sady 300 m. Quiet one-way street. Available from August 1.',
  },
  Punchy: {
    title: '58 m² of Vinohrady with a balcony you’ll actually use',
    body: 'Renovated top to bottom in 2024. South balcony over a quiet street. Park at the end of the block, metro four minutes the other way. Two rooms, zero compromises. Come see it before someone quicker does.',
  },
}

// The "paste my old ad" path — messy in, clean out.
export const MESSY_PASTE =
  '!!!NICE FLAT IN CENTRUM!!! 2+kk VINOHRADY 58m FULLY RENOVATED!! new kitchen+bathroom, balcony SOUTH, 4 floor lift OK. NO AGENTS!!! serious inquiries only, call 777 123 456 anytime. also selling washing machine cheap. near park and metro, MUST SEE!!!'
export const CLEANED_PASTE = {
  title: 'Renovated 2+kk with south balcony, Vinohrady',
  body: 'Renovated 2+kk (58 m²) in Vinohrady — new kitchen and bathroom, south-facing balcony, 4th floor with elevator. Close to Riegrovy sady and metro A at Jiřího z Poděbrad.',
  removed:
    'Removed: 14 exclamation marks, ALL-CAPS, a phone number (that belongs in your contact details) and one washing machine for sale.',
} as const

// ── My listings (admin panel) seed ──────────────────────────────────────────
export const MY_LISTINGS_SEED: MyListing[] = [
  {
    id: 'm1',
    title: 'Garden studio with terrace, Nusle',
    status: 'live',
    price: 16900,
    rooms: 1,
    area: 38,
    floor: 1,
    totalFloors: 4,
    district: 'Nusle',
    palette: 8,
    images: 6,
    createdLabel: '24 days ago',
    saves: 41,
    updates: [{ id: 'u1', text: 'Viewings this Saturday 10:00–14:00 — three slots left.', when: '2d ago' }],
    suggestions: [
      {
        id: 'sg1',
        icon: 'sell',
        text: 'Priced 6% above similar studios in Nusle — at 15 900 Kč you’d sit in the fair band and roughly double weekly interest.',
        action: 'Adjust to 15 900 Kč',
        apply: { price: 15900 },
      },
    ],
  },
  {
    id: 'm2',
    title: '3+kk with mezzanine, Libeň',
    status: 'paused',
    price: 28900,
    rooms: 3,
    area: 81,
    floor: 5,
    totalFloors: 6,
    district: 'Libeň',
    palette: 5,
    images: 9,
    createdLabel: 'paused 12 days ago',
    saves: 27,
    suggestions: [
      {
        id: 'sg3',
        icon: 'schedule',
        text: 'Paused for 12 days — listings lose their search ranking after 14. Resume now to keep your position.',
        action: 'Resume listing',
        apply: { status: 'live' },
      },
    ],
  },
  {
    id: 'm3',
    title: 'Parking spot, Karlín riverside',
    status: 'draft',
    price: 2400,
    rooms: 0,
    area: 12,
    floor: -1,
    totalFloors: 8,
    district: 'Karlín',
    palette: 9,
    images: 2,
    createdLabel: 'draft · 60% done',
    saves: 0,
    suggestions: [
      {
        id: 'sg4',
        icon: 'edit_note',
        text: 'Two steps left: a price and one photo of the entrance. Takes about a minute in the composer.',
        action: 'Finish listing',
        goPost: true,
      },
    ],
  },
]

// ── Deterministic "tidy" pass — strips shouting, phone numbers, formats
// sentences (prototype postform.jsx tidyText/tidyNote, ported verbatim) ──────

export interface TidyRemoved {
  bangs: number
  caps: number
  phone: number
}

export interface TidyResult {
  text: string
  removed: TidyRemoved
  changed: boolean
}

export function tidyText(raw: string): TidyResult {
  const removed: TidyRemoved = { bangs: 0, caps: 0, phone: 0 }
  let s = raw
  const phones = s.match(/\+?\d[\d\s-]{7,}\d/g)
  if (phones) {
    removed.phone = phones.length
    s = s.replace(/\+?\d[\d\s-]{7,}\d/g, '')
  }
  const bangs = s.match(/!+/g)
  if (bangs) {
    removed.bangs = bangs.reduce((a, b) => a + b.length, 0)
    s = s.replace(/\s*!+/g, '.')
  }
  const capsWords = s.match(/\b[A-ZÁ-Ž]{3,}\b/g)
  if (capsWords) {
    removed.caps = capsWords.length
    s = s.replace(/\b([A-ZÁ-Ž]{3,})\b/g, (m) => m.charAt(0) + m.slice(1).toLowerCase())
  }
  s = s
    .replace(/\.{2,}/g, '.')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+\./g, '.')
    .replace(/\.([^\s.])/g, '. $1')
    .trim()
  s = s.replace(/(^|[.?!]\s+)([a-zá-ž])/g, (m, p: string, c: string) => p + c.toUpperCase())
  const n = removed.bangs + removed.caps + removed.phone
  return { text: s, removed, changed: n > 0 }
}

export function tidyNote(r: TidyRemoved): string {
  const bits: string[] = []
  if (r.bangs) bits.push(`${r.bangs} exclamation mark${r.bangs > 1 ? 's' : ''}`)
  if (r.caps) bits.push(`${r.caps} ALL-CAPS word${r.caps > 1 ? 's' : ''}`)
  if (r.phone) bits.push('a phone number (moved to your contact details)')
  return bits.join(', ')
}
